import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Eye, EyeOff } from 'lucide-react';
import AuthShell from '@/components/AuthShell';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (document.cookie.includes('pp_csrf')) navigate('/dashboard');
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setError('');
    setLoading(true);
    try {
      await api.post(`/auth/login`, { email, password });
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'email_not_verified') {
        toast.error('Почта не подтверждена');
        navigate(`/verify-email?email=${encodeURIComponent(err.response.data.email)}`);
      } else {
        setError(typeof detail === 'string' ? detail : 'Неверный email или пароль');
      }
    }
    setLoading(false);
  };

  return (
    <AuthShell>
      <div data-testid="login-page">
        <h1 className="font-heading text-xl mb-1" style={{ color: 'var(--app-text)' }}>Вход в кабинет</h1>
        <p className="text-sm mb-6" style={{ color: 'var(--app-text-muted)' }}>Рады видеть вас снова</p>

        <form onSubmit={handleLogin} className="space-y-4" noValidate>
          {error && (
            <div role="alert" className="px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)' }}>
              {error}
            </div>
          )}
          <div>
            <label htmlFor="login-email" className="text-stone-400 text-sm font-medium mb-2 block">Email</label>
            <Input id="login-email" name="email" data-testid="email-input" type="email" autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com" className="bg-stone-950/50 border-stone-800 focus:border-amber-700 text-stone-200 placeholder:text-stone-600 h-11" autoFocus />
          </div>
          <div>
            <label htmlFor="login-password" className="text-stone-400 text-sm font-medium mb-2 block">Пароль</label>
            <div className="relative">
              <Input id="login-password" name="password" data-testid="password-input" type={showPassword ? 'text' : 'password'} autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Введите пароль" className="bg-stone-950/50 border-stone-800 focus:border-amber-700 text-stone-200 placeholder:text-stone-600 h-11 pr-11" />
              <button type="button" onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-white/5 transition-colors"
                style={{ color: 'var(--app-text-muted)' }}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <Button data-testid="login-submit-btn" type="submit" disabled={loading}
            className="w-full bg-amber-700 hover:bg-amber-600 text-white h-11 font-medium transition-all active:scale-[0.98]">
            {loading ? 'Вход...' : 'Войти'}
          </Button>
        </form>

        <div className="text-center mt-5">
          <Link to="/recover" className="text-stone-500 text-xs hover:text-amber-400 transition-colors">Забыли пароль?</Link>
        </div>

        <p className="text-stone-500 text-sm text-center mt-4">
          Нет аккаунта?{' '}
          <Link to="/register" className="text-amber-500 hover:text-amber-400 transition-colors">Зарегистрироваться</Link>
        </p>
      </div>
    </AuthShell>
  );
}
